import type { KevelDecision } from '../../shared/decisionResponse'

export interface ParsedKevelAd {
    /** The primary asset URL (image or video). */
    assetUrl: string
    /** Whether the asset is a video (true) or image (false). */
    isVideo: boolean
    /** Optional headline text. */
    headline?: string
    /** Optional subline/description text. */
    subline?: string
    /** Optional click-through target: the Shopware category id (see validateKevelTargetUrl). */
    targetUrl?: string
    /** Background color (if provided). */
    backgroundColor?: string
    /** Background image URL (if provided). */
    backgroundImage?: string
    /** Product numbers array (if provided). */
    products?: string[]
    /** Brand asset URL (if provided). */
    brandAsset?: string
    /** Kevel's tracking click URL. */
    clickUrl: string
    /** Kevel's tracking impression URL. */
    impressionUrl: string
}

/**
 * Parses a Kevel decision into a renderable ad object. Reads from `contents[0].data`
 * (the parsed creative payload) rather than parsing `body`, per the API gotchas doc.
 *
 * Returns null if the decision is null (no-fill) or malformed.
 */
export function parseKevelDecision(decision: KevelDecision | null): ParsedKevelAd | null {
    if (decision == null) {
        return null
    }

    const content = decision.contents?.[0]
    if (content == null) {
        console.error('[kevel] decision missing contents array', { decision })
        return null
    }

    const data = content.data
    if (data == null) {
        console.error('[kevel] decision content missing data', { decision })
        return null
    }

    // Determine primary asset URL (ctAsset takes priority over imageUrl)
    const assetUrl = data.ctAsset ?? data.imageUrl ?? data.externalUrl
    if (assetUrl == null) {
        console.error('[kevel] decision missing asset URL', { decision })
        return null
    }

    // Detect video vs image by file extension (ignoring query parameters)
    const isVideo = /\.(mp4|webm|mov)(\?|$)/i.test(assetUrl)

    return {
        assetUrl,
        isVideo,
        headline: data.ctHeadline,
        subline: data.ctSubline,
        targetUrl: data.ctTargetUrl,
        backgroundColor: data.ctBackgroundColor,
        backgroundImage: data.ctBackgroundImage,
        products: data.ctProducts,
        brandAsset: data.ctBrandAsset,
        clickUrl: decision.clickUrl,
        impressionUrl: decision.impressionUrl,
    }
}
